import { useState } from "react";
import { Check, Zap, Bot, MessageSquare, Shield, Rocket, HelpCircle, Crown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useParams } from "react-router-dom";

export function Premium() {
  const { guildId } = useParams();
  const { data: me } = useQuery<any>({ queryKey: ["me"], queryFn: api.me });
  const { data: info } = useQuery<any>({
    queryKey: ["premium-info", guildId],
    queryFn: () => api.premiumInfo(guildId!),
    enabled: !!guildId,
  });

  const getCheckoutUrl = (baseUrl: string, planKey: string) => {
    let url = baseUrl;
    const params = [];
    if (me?.id) params.push(`checkout[custom][discord_user_id]=${me.id}`);
    if (guildId) params.push(`checkout[custom][discord_guild_id]=${guildId}`);
    params.push(`checkout[custom][plan]=${planKey}`);
    if (params.length) {
      url += `?${params.join("&")}`;
    }
    return url;
  };

  const PLANS = [
    {
      name: "Monthly",
      price: "4.99",
      per: "/month",
      tagline: "Test the waters",
      description: "Perfect for starting out and testing all premium features.",
      features: ["AI Ticket Pilot", "Global AI FAQ", "White-label Bot", "Advanced Logging", "Priority Support"],
      url: "https://areon.lemonsqueezy.com/checkout/buy/41eb3d46-784d-44a4-a146-8c8025803c37",
    },
    {
      name: "Yearly",
      price: "49.99",
      per: "/year",
      tagline: "Save over 25%",
      description: "Best value for growing communities. Save over 25%!",
      features: ["Everything in Monthly", "Exclusive Badge", "Early Beta Access", "Advanced AI Architect", "24/7 Monitoring"],
      url: "https://areon.lemonsqueezy.com/checkout/buy/57d18d17-4434-4c5a-b5a8-68b76fa55095",
      popular: true,
    },
    {
      name: "Lifetime",
      price: "149.99",
      per: "one-time",
      tagline: "Pay once, own forever",
      description: "One-time payment. Own the ultimate Areon experience forever.",
      features: ["Everything in Yearly", "Lifetime Updates", "VIP Role in Discord", "Custom Features Request", "Personal Manager"],
      url: "https://areon.lemonsqueezy.com/checkout/buy/e75741b0-8739-41f9-8265-b2c79c71cb25",
    }
  ];

  const [selected, setSelected] = useState(1);
  const active = PLANS[selected];

  return (
    <div className="py-8 sm:py-10">
      <div className="max-w-6xl mx-auto">
        {/* Heading — left aligned */}
        <div className="flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-medium text-primary w-fit mb-3">
          <Zap className="h-3 w-3" />
          Areon Premium
        </div>
        <h1 className="text-2xl sm:text-4xl font-semibold tracking-tight">
          Choose your plan
        </h1>
        <p className="text-[13px] sm:text-sm text-muted-foreground mt-1.5 max-w-xl leading-relaxed">
          Unlock industry-leading AI, white-labeling and advanced management. Pick a plan on the left, check out on the right.
        </p>

        {/* ===== Split: selector (left) + checkout panel (right) ===== */}
        <div className="grid lg:grid-cols-[1fr_400px] gap-5 mt-8 items-start">
          {/* LEFT — plan selector rows */}
          <div className="space-y-3">
            {PLANS.map((plan, i) => {
              const isSel = selected === i;
              return (
                <button
                  key={plan.name}
                  type="button"
                  onClick={() => setSelected(i)}
                  className={`w-full text-left rounded-2xl border p-4 sm:p-5 transition-all flex items-center gap-4 ${
                    isSel
                      ? "border-primary/50 bg-primary/5 ring-1 ring-primary/30"
                      : "border-border/50 bg-card/40 hover:bg-card/70 hover:border-border"
                  }`}
                >
                  {/* Radio */}
                  <span
                    className={`h-5 w-5 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${
                      isSel ? "border-primary" : "border-muted-foreground/40"
                    }`}
                  >
                    {isSel && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{plan.name}</span>
                      {plan.popular && (
                        <Badge className="bg-primary text-primary-foreground px-1.5 py-0 text-[9px] font-semibold border-none">
                          BEST VALUE
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{plan.tagline}</p>
                  </div>

                  <div className="text-right shrink-0">
                    <div className="text-lg font-semibold tracking-tight">${plan.price}</div>
                    <div className="text-[11px] text-muted-foreground">{plan.per}</div>
                  </div>
                </button>
              );
            })}

            {/* Manual payment */}
            <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-5 mt-4">
              <div className="flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[11px] font-medium text-amber-400 w-fit mb-2.5">
                <HelpCircle className="h-3 w-3" />
                Manual payments
              </div>
              <h2 className="text-sm font-semibold mb-1">Prefer another payment method?</h2>
              <p className="text-[13px] text-muted-foreground leading-relaxed mb-3">
                Revolut, IBAN transfer, cash, or crypto — open a{" "}
                <b className="text-foreground/80">support ticket</b> on Discord and we'll activate Premium manually.
              </p>
              <Button
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
                onClick={() => window.open(info?.support_server_url || "https://discord.gg/8eh7KrMPXg", "_blank")}
              >
                <HelpCircle className="mr-1 h-4 w-4" />
                Open Discord Ticket
              </Button>
            </div>
          </div>

          {/* RIGHT — sticky checkout panel for selected plan */}
          <div className="lg:sticky lg:top-6">
            <Card className="relative overflow-hidden border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-6">
              <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-primary/20 blur-3xl" />
              <AnimatePresence mode="wait">
                <motion.div
                  key={active.name}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.18 }}
                  className="relative"
                >
                  <div className="flex items-center gap-2 text-primary">
                    <Crown className="h-4 w-4" />
                    <span className="text-xs font-semibold uppercase tracking-wider">{active.name} plan</span>
                  </div>
                  <div className="flex items-baseline gap-1.5 mt-3">
                    <span className="text-4xl font-semibold tracking-tight">${active.price}</span>
                    <span className="text-sm text-muted-foreground">{active.per}</span>
                  </div>
                  <p className="text-[13px] text-muted-foreground mt-2 leading-relaxed">
                    {active.description}
                  </p>

                  <div className="h-px bg-border/50 my-5" />

                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    What's included
                  </p>
                  <div className="space-y-2.5">
                    {active.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-2.5 text-sm">
                        <div className="h-4 w-4 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                          <Check className="h-2.5 w-2.5 text-primary" strokeWidth={3} />
                        </div>
                        <span className="text-foreground/80">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    className="w-full h-12 mt-6 text-base font-medium"
                    onClick={() => window.open(getCheckoutUrl(active.url, active.name.toLowerCase()), "_blank")}
                  >
                    Get {active.name}
                    <Rocket className="ml-1.5 h-4 w-4" />
                  </Button>
                  <p className="text-[11px] text-center text-muted-foreground mt-3">
                    Secure checkout via Lemon Squeezy
                  </p>
                </motion.div>
              </AnimatePresence>
            </Card>
          </div>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 border-t border-border/40 pt-10 mt-12">
          {[
            { icon: MessageSquare, color: "blue", title: "AI Assistant", desc: "24/7 automated support that actually works." },
            { icon: Bot, color: "purple", title: "Custom Brand", desc: "Run Areon under your own name and token." },
            { icon: Shield, color: "orange", title: "Safe & Reliable", desc: "Bank-level security for your server data." },
            { icon: Zap, color: "emerald", title: "Instant Setup", desc: "Get started in seconds with zero configuration." },
          ].map((f) => {
            const Icon = f.icon;
            const colorMap: Record<string, string> = {
              blue: "bg-blue-500/10 text-blue-500",
              purple: "bg-purple-500/10 text-purple-500",
              orange: "bg-orange-500/10 text-orange-500",
              emerald: "bg-emerald-500/10 text-emerald-500",
            };
            return (
              <Card key={f.title} className="p-5 border-border/50 bg-card/40 hover:bg-card/70 transition-colors">
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center mb-3 ${colorMap[f.color]}`}>
                  <Icon className="h-4.5 w-4.5" />
                </div>
                <h4 className="font-medium text-sm mb-1">{f.title}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
